/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { Filter } from "@gaming/xds-react-js-icons";

import {
  GameConceptApplication,
  GameConceptFilter,
  GameConceptFiltersForTab,
  GameConceptTab,
  usePaginatedGameConcepts,
} from "../../helpers/gameConceptHelpers";
import { getDashboardUrl } from "../../helpers/navigationHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { Programs } from "../../models/programsConfig";
import { RouteNames } from "../../partnerConfig/routeNames";
import { gameConceptActions } from "../../redux/actions/gameConceptActions";
import { getIsLoadingGameConcept } from "../../redux/selectors";
import { getGameConceptsQuery } from "../../redux/selectors/crmStatusSelectors";
import {
  AaaGameConceptDataSet,
  GameConceptsQuery,
  IdXboxGameConceptDataSet,
} from "../../server/crm/api/models/forms";
import { PaginatedConceptData } from "../../server/crm/api/responses/getFullPaginatedConceptsResponse";
import { ConceptsManager, ConceptsManagerConfig, conceptsManagerStyles } from "../ConceptsManager";
import { SelectDropdown } from "../SelectDropdown/SelectDropdown";
import { GameConceptListRow } from "./GameConceptListRow";

export type GameConceptsManagerProps = Readonly<{
  programDashboardRouteName: RouteNames;
  program: Programs;
  onNewConcept?: () => void;
  onConceptClick?: (gameConceptId: string) => void;
  gameConceptsQuerySelector?: (state: any) => GameConceptsQuery | undefined;
  gameConceptsListSelector?: (
    state: any
  ) => AaaGameConceptDataSet | IdXboxGameConceptDataSet | undefined;
  isLoadingSelector?: (state: any) => boolean;
  onFetchGameConcepts?: (paginatedInfo: PaginatedConceptData) => void;
  onClearQuery?: () => void;
  hideFilters?: boolean;
  canCreate?: boolean;
}>;

const initialTab = GameConceptTab.All;

const mergeGameConceptPages = (
  existingConcepts: GameConceptApplication[],
  newConcepts: GameConceptApplication[]
): GameConceptApplication[] => {
  const conceptsById = new Map(existingConcepts.map(concept => [concept.id, concept]));
  newConcepts.forEach(concept => conceptsById.set(concept.id, concept));
  return Array.from(conceptsById.values());
};

export const GameConceptsManager = ({
  programDashboardRouteName,
  program,
  onNewConcept,
  onConceptClick,
  gameConceptsQuerySelector,
  gameConceptsListSelector,
  isLoadingSelector,
  onFetchGameConcepts,
  onClearQuery,
  canCreate = true,
}: GameConceptsManagerProps) => {
  const [
    welcomePageGameConceptsListEmpty,
    managerTitle,
    createButtonLabel,
    searchPlaceholder,
    emptyStateTitle,
    emptyStateDescription,
    filterAll,
    draftLabel,
    underReviewLabel,
    onHoldLabel,
    needMoreInformationLabel,
    approvedLabel,
    rejectedLabel,
    filterAriaLabel,
    statusAriaLabel,
  ] = useStrings(
    "welcomePageGameConceptsListEmpty",
    "gameConceptsManagerTitle",
    "gameConceptsManagerCreateButton",
    "gameConceptsManagerSearchPlaceholder",
    "gameConceptsManagerEmptyStateTitle",
    "gameConceptsManagerEmptyStateDescription",
    "filterAll",
    "draft",
    "underReview",
    "onHold",
    "needMoreInformation",
    "approved",
    "rejected",
    "filterAriaLabel",
    "statusAriaLabel"
  );
  const gameConceptConfig: ConceptsManagerConfig = {
    title: managerTitle,
    createButtonLabel,
    searchPlaceholder,
    emptyStateTitle,
    emptyStateDescription,
    emptyStateButtonLabel: createButtonLabel,
  };
  const history = useHistory();
  const dispatch = useDispatch();
  const defaultGameConceptsQueries = useSelector(getGameConceptsQuery);
  const customGameConceptsQueries = useSelector(gameConceptsQuerySelector ?? (() => undefined));
  const gameConceptsQueries = gameConceptsQuerySelector
    ? customGameConceptsQueries
    : defaultGameConceptsQueries;
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [paginatedInformation, setPaginatedInformation] = useState({
    currentPage: 1,
    orderBy: "",
    filterBy: GameConceptFiltersForTab[initialTab],
    searchBy: "",
  } as PaginatedConceptData);

  const [isInitialCall, setIsInitialCall] = useState(true);
  const totalPages = Number(gameConceptsQueries?.totalPages);

  const defaultIsLoading = useSelector(getIsLoadingGameConcept);
  const customIsLoading = useSelector(isLoadingSelector ?? (() => false));
  const isLoadingGameConcepts = isLoadingSelector ? customIsLoading : defaultIsLoading;

  // Accumulated concepts across pages for infinite scroll
  const [accumulatedConcepts, setAccumulatedConcepts] = useState<GameConceptApplication[]>([]);
  // Guard against rapid-fire triggers from IntersectionObserver
  const isFetchingRef = useRef(false);
  const pendingAppendTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if ((!gameConceptsQueries && isInitialCall) || (gameConceptsQueries && !isInitialCall)) {
      isFetchingRef.current = true;
      if (onFetchGameConcepts) {
        onFetchGameConcepts(paginatedInformation);
      } else {
        dispatch(gameConceptActions.getGameConcepts.start(paginatedInformation, program));
      }
    }
    setIsInitialCall(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedInformation, program, dispatch]);

  useEffect(
    () => () => {
      if (onClearQuery) {
        onClearQuery();
      } else {
        dispatch(gameConceptActions.clearGameConceptsListQuery(program));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const defaultGameConcepts = usePaginatedGameConcepts();
  const customGameConceptsList = useSelector(gameConceptsListSelector ?? (() => undefined));
  const customGameConcepts = useMemo(
    () =>
      customGameConceptsList
        ? Object.entries(customGameConceptsList).map(([id, gameConceptInfo]) => ({
            id,
            gameConceptInfo,
          }))
        : undefined,
    [customGameConceptsList]
  );
  const latestPageConcepts = gameConceptsListSelector ? customGameConcepts : defaultGameConcepts;

  // When loading finishes, show spinner for 1 second then append new data
  const wasLoadingRef = useRef(false);
  const hasCapturedFirstPageRef = useRef(false);
  useEffect(() => {
    if (isLoadingGameConcepts) {
      wasLoadingRef.current = true;
      return;
    }

    if (paginatedInformation.currentPage === 1) {
      const shouldReplaceFirstPage =
        wasLoadingRef.current ||
        (!hasCapturedFirstPageRef.current && gameConceptsQueries !== undefined);
      wasLoadingRef.current = false;
      isFetchingRef.current = false;
      hasCapturedFirstPageRef.current = true;
      if (shouldReplaceFirstPage) {
        setAccumulatedConcepts(latestPageConcepts ?? []);
      }
      setShowLoadingMore(false);
      return;
    }

    if (!wasLoadingRef.current) {
      return;
    }

    wasLoadingRef.current = false;
    const elapsed = Date.now() - loadMoreStartTimeRef.current;
    const remaining = Math.max(0, 1000 - elapsed);
    const newConcepts = latestPageConcepts ?? [];
    pendingAppendTimeoutRef.current = setTimeout(() => {
      if (newConcepts.length > 0) {
        setAccumulatedConcepts(previousConcepts =>
          mergeGameConceptPages(previousConcepts, newConcepts)
        );
      }
      pendingAppendTimeoutRef.current = undefined;
      isFetchingRef.current = false;
      setShowLoadingMore(false);
    }, remaining);

    return () => {
      if (pendingAppendTimeoutRef.current) {
        clearTimeout(pendingAppendTimeoutRef.current);
        pendingAppendTimeoutRef.current = undefined;
      }
    };
  }, [
    gameConceptsQueries,
    isLoadingGameConcepts,
    latestPageConcepts,
    paginatedInformation.currentPage,
  ]);

  const gameConcepts = accumulatedConcepts.length > 0 ? accumulatedConcepts : latestPageConcepts;
  const gotGameConcepts = gameConcepts && gameConcepts.length > 0;
  const totalCount = Number(gameConceptsQueries?.totalCount ?? 0);
  const hasActiveFilters = paginatedInformation.searchBy !== "" || statusFilter !== "all";
  const hasNeverCreatedConcepts =
    !isLoadingGameConcepts && totalCount === 0 && !gotGameConcepts && !hasActiveFilters;
  const hasMore = !isNaN(totalPages) && paginatedInformation.currentPage < totalPages;

  // Show spinner for at least 1 second when loading more
  const [showLoadingMore, setShowLoadingMore] = useState(false);
  const loadMoreStartTimeRef = useRef<number>(0);

  const handleLoadMore = useCallback(() => {
    if (!isFetchingRef.current && !isLoadingGameConcepts && hasMore) {
      isFetchingRef.current = true;
      setShowLoadingMore(true);
      loadMoreStartTimeRef.current = Date.now();
      setPaginatedInformation(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
    }
  }, [isLoadingGameConcepts, hasMore]);

  const handleCreateNew = () => {
    if (onNewConcept) {
      onNewConcept();
    } else {
      history.push(getDashboardUrl(programDashboardRouteName, uuidv4(), true));
    }
  };

  const handleStatusFilterChange = (filter: string): void => {
    setStatusFilter(filter);
    let filterBy: GameConceptFilter[] = GameConceptFiltersForTab[GameConceptTab.All];

    switch (filter) {
      case "draft":
        filterBy = [GameConceptFilter.Draft];
        break;
      case "underreview":
        filterBy = [GameConceptFilter.UnderReview];
        break;
      case "onhold":
        filterBy = [GameConceptFilter.OnHold];
        break;
      case "needmoreinformation":
        filterBy = [GameConceptFilter.NeedMoreInfo];
        break;
      case "approved":
        filterBy = [GameConceptFilter.Approved];
        break;
      case "rejected":
        filterBy = [GameConceptFilter.Rejected];
        break;
    }

    if (pendingAppendTimeoutRef.current) {
      clearTimeout(pendingAppendTimeoutRef.current);
      pendingAppendTimeoutRef.current = undefined;
    }
    setAccumulatedConcepts([]);
    isFetchingRef.current = false;
    wasLoadingRef.current = false;
    setPaginatedInformation({
      currentPage: 1,
      orderBy: paginatedInformation.orderBy,
      filterBy,
      searchBy: paginatedInformation.searchBy,
    });
  };

  // GameConceptsManager uses GameConceptListRow which has complex logic (validation, viewOnly routing).
  // We pass stub ConceptItems for count/empty-state logic and use renderRow for actual rendering.
  return (
    <ConceptsManager
      config={{ ...gameConceptConfig, noResultsMessage: welcomePageGameConceptsListEmpty }}
      items={
        gameConcepts?.map((gc, i) => ({
          id: `${i}-${gc.id}`,
          title: gc.gameConceptInfo.details?.gameTitle ?? "",
          statusLabel: "",
          statusDotClass: "",
          statusLabelClass: "",
          datePrefix: "",
          formattedDate: null,
          onClick: () => {},
        })) ?? undefined
      }
      isLoading={isLoadingGameConcepts && paginatedInformation.currentPage === 1}
      hasNeverCreated={hasNeverCreatedConcepts}
      onCreateNew={canCreate ? handleCreateNew : undefined}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      isLoadingMore={showLoadingMore}
      onSearchChange={searchBy => {
        if (pendingAppendTimeoutRef.current) {
          clearTimeout(pendingAppendTimeoutRef.current);
          pendingAppendTimeoutRef.current = undefined;
        }
        setAccumulatedConcepts([]);
        isFetchingRef.current = false;
        wasLoadingRef.current = false;
        setPaginatedInformation({ ...paginatedInformation, searchBy, currentPage: 1 });
      }}
      renderRow={(_item, index) => {
        const gameConcept = gameConcepts?.[index];
        if (!gameConcept) {
          return null;
        }
        return (
          <GameConceptListRow
            gameConcept={gameConcept}
            program={program}
            programDashboardRouteName={programDashboardRouteName}
            onConceptClick={onConceptClick}
          />
        );
      }}
      renderHeaderActions={() => (
        <button
          type="button"
          className={conceptsManagerStyles.iconButton}
          onClick={() => setShowFilterPopup(!showFilterPopup)}
          aria-label={filterAriaLabel}
        >
          <Filter />
        </button>
      )}
      renderBelowHeader={() =>
        showFilterPopup ? (
          <div className={conceptsManagerStyles.filterPopup}>
            <div className={conceptsManagerStyles.filterDropdown}>
              <SelectDropdown
                aria-label={statusAriaLabel}
                value={statusFilter}
                onChange={handleStatusFilterChange}
                options={[
                  { id: "all", label: filterAll },
                  { id: "draft", label: draftLabel },
                  { id: "underreview", label: underReviewLabel },
                  { id: "onhold", label: onHoldLabel },
                  { id: "needmoreinformation", label: needMoreInformationLabel },
                  { id: "approved", label: approvedLabel },
                  { id: "rejected", label: rejectedLabel },
                ]}
              />
            </div>
          </div>
        ) : null
      }
    />
  );
};
